import React, { FC, PropsWithChildren, createContext, useContext, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';

import { SortContext } from '../SortContext';
import { useEventsFilterable } from '../../events/common/EventsListContext';

import { LOAD_EVENTS_LIMIT } from './constants';
import { getEventsData } from 'services/events/helpers';
import { smoothScrollTo } from 'services/common/scrollHelper';

import { ListQuery } from 'types/graphql';
import { EventsType, SessionStorageKeys } from 'types/enums';

type TEventsContext = {
  eventsData: EventsListData;
  eventsResponse?: EventsListResponse;
  loading: boolean;
  fetchMore: Function;
  queryKey: EventsListQueryKey;
  isFilterApplied: boolean;
  isHistory: boolean;
};

type TEventsContextProvider = {
  query: ListQuery;
  queryKey: EventsListQueryKey;
};

export const EventsContext = createContext<TEventsContext>(undefined!);

export const useEvents = () => useContext(EventsContext);

export const EventsContextProvider: FC<PropsWithChildren<{ value: TEventsContextProvider }>> = ({
  children,
  value,
}) => {
  const { filter } = useEventsFilterable();
  const { sort } = useContext(SortContext);

  const isFilterApplied = !!filter.q || !!filter.statusId || !!filter.djId;

  const isHistory = value.queryKey === EventsType.historyEvents;

  const limit = useRef(
    +(sessionStorage.getItem(SessionStorageKeys.eventsCount) ?? LOAD_EVENTS_LIMIT)
  ).current;

  const { data: eventsResponse, loading, fetchMore } = useQuery<EventsListResponse>(value.query, {
    variables: {
      filter,
      sort,
      pagination: {
        skip: 0,
        limit,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const eventsData: EventsListData = getEventsData(eventsResponse);

  const handleFetchMore = () => {
    !loading &&
      eventsData.next &&
      fetchMore({
        variables: {
          filter,
          sort,
          pagination: {
            ...eventsData.next,
            limit: LOAD_EVENTS_LIMIT,
          },
        },
        updateQuery: (prev: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult) return prev;

          return {
            [value.queryKey]: {
              ...prev[value.queryKey],
              events: [...prev[value.queryKey].events, ...fetchMoreResult[value.queryKey].events],
              next: fetchMoreResult[value.queryKey].next,
            },
          };
        },
      });
  };

  const scrollY = sessionStorage.getItem(SessionStorageKeys.eventsScrollY);

  useEffect(() => {
    if (!!scrollY && !!eventsData.events.length) {
      setTimeout(() => {
        smoothScrollTo(+scrollY, () => {
          sessionStorage.removeItem(SessionStorageKeys.eventsScrollY);
          sessionStorage.removeItem(SessionStorageKeys.eventsCount);
        });
      });
    }
  }, [eventsData.events.length]);

  return (
    <EventsContext.Provider
      value={{
        eventsData,
        isHistory,
        isFilterApplied,
        loading,
        fetchMore: handleFetchMore,
        queryKey: value.queryKey,
      }}
    >
      {children}
    </EventsContext.Provider>
  );
};
